'use client'

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  Building2,
  DollarSign,
  Factory,
  Lightbulb,
  type LucideIcon,
  MapPin,
  Shield,
  Target,
  TrendingUp,
  Trophy,
  Users,
} from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { useRecipientDetail } from './context'

interface MinimapSection {
  id: string
  label: string
  shortLabel?: string
  icon: LucideIcon
}

// Hoisted outside component to avoid recreation (rendering-hoist-jsx)
// Order matches the layout order in SuspenseRecipientViewV2
const SECTIONS: MinimapSection[] = [
  {
    id: 'insights',
    label: 'Quick Insights',
    shortLabel: 'Insights',
    icon: Lightbulb,
  },
  {
    id: 'award-cash-flow',
    label: 'Cash Flow',
    shortLabel: 'Cash Flow',
    icon: TrendingUp,
  },
  {
    id: 'agencies',
    label: 'Top Agencies',
    shortLabel: 'Agencies',
    icon: Building2,
  },
  {
    id: 'industry',
    label: 'Industry Codes',
    shortLabel: 'Industry',
    icon: Factory,
  },
  { id: 'geography', label: 'Geography', icon: MapPin },
  { id: 'set-asides', label: 'Set-Asides', icon: Shield },
  { id: 'competition', label: 'Competition', icon: Target },
  {
    id: 'contract-sizes',
    label: 'Contract Sizes',
    shortLabel: 'Contracts',
    icon: DollarSign,
  },
  { id: 'officers', label: 'Officers', icon: Users },
  {
    id: 'recent-awards',
    label: 'Recent Awards',
    shortLabel: 'Awards',
    icon: Trophy,
  },
]

// Height of the tabs bar for scroll offset
const TABS_HEIGHT = 44

// Section IDs for intersection observer
const SECTION_IDS = SECTIONS.map((s) => s.id)

/**
 * Memoized tab button (rerender-memo)
 */
const TabButton = memo(function TabButton({
  section,
  isActive,
  sectionId,
  onNavigate,
}: {
  section: MinimapSection
  isActive: boolean
  sectionId: string
  onNavigate: (id: string) => void
}) {
  const Icon = section.icon
  const handleClick = useCallback(
    () => onNavigate(sectionId),
    [onNavigate, sectionId],
  )

  return (
    <button
      type="button"
      onClick={handleClick}
      className={twMerge(
        'flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150',
        isActive
          ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/50 dark:text-accent-300'
          : 'text-primary-500 hover:bg-primary-100 hover:text-primary-700 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-300',
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="whitespace-nowrap">
        {section.shortLabel ?? section.label}
      </span>
    </button>
  )
})

/**
 * Find the scrollable ancestor element.
 */
function findScrollableAncestor(
  element: HTMLElement | null,
): HTMLElement | null {
  if (!element) return null

  let current: HTMLElement | null = element.parentElement
  while (current) {
    const style = window.getComputedStyle(current)
    const overflowY = style.overflowY
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight
    ) {
      return current
    }
    current = current.parentElement
  }
  return null
}

/**
 * RecipientDetail.Minimap
 *
 * Horizontal tab navigation for sections. Starts in normal document flow,
 * becomes fixed when scrolled to keep tabs visible.
 *
 * Uses context for scroll/highlight (vercel-composition-patterns):
 * - scrollToSection handles scrolling + highlight timing
 * - Sections read highlight state from context
 */
export function RecipientMinimap() {
  const { scrollToSection, setScrollContainer, isProgrammaticScrolling } =
    useRecipientDetail()
  const [activeSection, setActiveSection] = useState<string>('insights')
  const [isStuck, setIsStuck] = useState(false)
  const placeholderRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLElement | null>(null)

  // Find scroll container and register it with context
  useEffect(() => {
    const placeholder = placeholderRef.current
    if (!placeholder) return

    const scrollContainer = findScrollableAncestor(placeholder)
    scrollContainerRef.current = scrollContainer

    // Register scroll container with context for scroll calculations
    setScrollContainer(scrollContainer)

    if (!scrollContainer) return

    const handleScroll = () => {
      const placeholderRect = placeholder.getBoundingClientRect()
      const containerRect = scrollContainer.getBoundingClientRect()
      const shouldStick = placeholderRect.top < containerRect.top
      setIsStuck(shouldStick)
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      setScrollContainer(null)
    }
  }, [setScrollContainer])

  // Track active section via intersection observer
  useEffect(() => {
    const observers: IntersectionObserver[] = []

    SECTION_IDS.forEach((sectionId) => {
      const element = document.getElementById(sectionId)
      if (!element) return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.2) {
              // Skip updates during programmatic scrolling to prevent tab flickering
              if (!isProgrammaticScrolling.current) {
                setActiveSection(sectionId)
              }
            }
          })
        },
        {
          threshold: [0.2],
          rootMargin: `-${TABS_HEIGHT + 20}px 0px -60% 0px`,
        },
      )

      observer.observe(element)
      observers.push(observer)
    })

    return () => {
      observers.forEach((o) => o.disconnect())
    }
  }, [isProgrammaticScrolling])

  // Click handler - uses context scrollToSection (composition pattern)
  // Scroll lock is managed by the context provider
  const handleClick = useCallback(
    (sectionId: string) => {
      setActiveSection(sectionId)
      scrollToSection(sectionId)
    },
    [scrollToSection],
  )

  return (
    <>
      {/* Placeholder - always rendered to maintain height in document flow */}
      <div
        ref={placeholderRef}
        className={twMerge(
          'w-full border-b border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-950',
          isStuck ? 'invisible' : '',
        )}
      >
        <nav className="w-full">
          <NavContent activeSection={activeSection} onNavigate={handleClick} />
        </nav>
      </div>

      {/* Fixed version - shown when scrolled past original position */}
      {isStuck && scrollContainerRef.current && (
        <FixedNav
          scrollContainer={scrollContainerRef.current}
          activeSection={activeSection}
          onNavigate={handleClick}
        />
      )}
    </>
  )
}

/**
 * Memoized nav content to avoid recreation on every render (rendering-hoist-jsx)
 */
const NavContent = memo(function NavContent({
  activeSection,
  onNavigate,
}: {
  activeSection: string
  onNavigate: (id: string) => void
}) {
  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8">
      <div className="flex gap-1 overflow-x-auto py-2 scrollbar-none">
        {SECTIONS.map((section) => (
          <TabButton
            key={section.id}
            section={section}
            isActive={activeSection === section.id}
            sectionId={section.id}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  )
})

/**
 * Fixed navigation that appears when scrolled.
 * Uses state to cache getBoundingClientRect values to avoid layout thrashing (js-cache-property-access)
 * Only listens to resize events since scroll container position only changes on resize (client-event-listeners)
 */
const FixedNav = memo(function FixedNav({
  scrollContainer,
  activeSection,
  onNavigate,
}: {
  scrollContainer: HTMLElement
  activeSection: string
  onNavigate: (id: string) => void
}) {
  const [position, setPosition] = useState({ top: 0, left: 0, right: 0 })

  useEffect(() => {
    const updatePosition = () => {
      const rect = scrollContainer.getBoundingClientRect()
      setPosition({
        top: rect.top,
        left: rect.left,
        right: window.innerWidth - rect.right,
      })
    }

    updatePosition()

    // Only resize changes the container's viewport position (not scroll)
    window.addEventListener('resize', updatePosition, { passive: true })

    return () => {
      window.removeEventListener('resize', updatePosition)
    }
  }, [scrollContainer])

  // Memoize style object to avoid recreation (rerender-memo-with-default-value)
  const navStyle = useMemo(
    () => ({
      top: position.top,
      left: position.left,
      right: position.right,
    }),
    [position.top, position.left, position.right],
  )

  return (
    <nav
      className={twMerge(
        'fixed z-30',
        'border-b border-primary-200 bg-white/95 backdrop-blur',
        'dark:border-primary-800 dark:bg-primary-950/95',
      )}
      style={navStyle}
    >
      <NavContent activeSection={activeSection} onNavigate={onNavigate} />
    </nav>
  )
})
